import {
  ChartColumn,
  Columns3,
  FileText,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Map,
  Monitor,
  Settings,
  SlidersHorizontal,
  SquarePen,
  User,
  type LucideIcon,
} from 'lucide-react';
import type { NavItem } from '../components';
import { ROUTES, type NavSection, type RouteDef } from '../routes/manifest';
import { ACCESS_LEVEL, type SessionUser } from './session-context';

function allowed(r: RouteDef, user: SessionUser | null): boolean {
  if (r.access === 'public' || r.access === 'anonymous-only') return true;
  return (user?.accessLevel ?? 0) >= ACCESS_LEVEL[r.access];
}

/** Icons named in the manifest (explicit imports keep the bundle tree-shaken). */
const ICONS: Record<string, LucideIcon> = {
  ChartColumn,
  Columns3,
  FileText,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Map,
  Monitor,
  Settings,
  SlidersHorizontal,
  SquarePen,
  User,
};

function icon(name: string | undefined) {
  if (!name) return undefined;
  const Icon = ICONS[name];
  return Icon ? <Icon className="size-4" /> : undefined;
}

export function navRoutes(section: NavSection, user: SessionUser | null): RouteDef[] {
  return (ROUTES as readonly RouteDef[])
    .filter((r) => r.nav?.section === section && allowed(r, user))
    .sort((a, b) => a.nav!.order - b.nav!.order);
}

/**
 * Sidebar = the legacy main menu (My view, View issues, Report issue, Change log, Roadmap,
 * Summary, Docs, Manage). Manage expands to the legacy manage_* tabs the user may open.
 */
export function sidebarItems(user: SessionUser | null): NavItem[] {
  const manage = navRoutes('manage', user);
  return navRoutes('main', user).map((r) => ({
    label: r.nav!.label ?? r.title,
    to: r.path,
    ...(r.path === '/' ? { end: true } : {}),
    ...(icon(r.nav!.icon) ? { icon: icon(r.nav!.icon) } : {}),
    ...(r.id === 'manage' && manage.length
      ? {
          children: [
            { label: 'Overview', to: '/manage', end: true },
            ...manage.map((m) => ({ label: m.nav!.label ?? m.title, to: m.path })),
          ],
        }
      : {}),
  }));
}
